export type ErrorType = 
  | 'NETWORK_ERROR'
  | 'VALIDATION_ERROR' 
  | 'AUTHENTICATION_ERROR'
  | 'PERMISSION_ERROR'
  | 'REGISTRATION_ERROR'
  | 'DATABASE_ERROR'
  | 'UNKNOWN_ERROR'

export class AppError extends Error {
  type: ErrorType
  originalError?: Error
  context?: Record<string, any>
  timestamp: Date
  userMessage: string
  actionable: boolean
  recoverable: boolean

  constructor(
    type: ErrorType,
    message: string,
    originalError?: Error,
    context?: Record<string, any>
  ) {
    super(message)
    this.name = 'AppError'
    this.type = type
    this.originalError = originalError
    this.context = context
    this.timestamp = new Date()
    this.userMessage = this.getUserMessage(type, message)
    this.actionable = this.isActionable(type)
    this.recoverable = this.isRecoverable(type)
  }

  private getUserMessage(type: ErrorType, message: string): string {
    const errorMessages: Record<ErrorType, string> = {
      NETWORK_ERROR: 'בעיה בחיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.',
      VALIDATION_ERROR: 'אנא בדוק שמילאת את כל השדות הנדרשים בצורה נכונה.',
      AUTHENTICATION_ERROR: 'בעיה בהתחברות למערכת. אנא נסה להתחבר שוב.',
      PERMISSION_ERROR: 'אין לך הרשאה לבצע פעולה זו. אנא פנה למנהל המערכת.',
      REGISTRATION_ERROR: 'בעיה ברישום לתורנות. ייתכן שהמקום כבר תפוס. אנא רענן את הדף ונסה שוב.',
      DATABASE_ERROR: 'בעיה זמנית במערכת. אנא נסה שוב בעוד מספר דקות.',
      UNKNOWN_ERROR: 'אירעה שגיאה לא צפויה. אנא נסה שוב או פנה למנהל המערכת.'
    }

    return errorMessages[type] || errorMessages.UNKNOWN_ERROR
  }

  private isActionable(type: ErrorType): boolean {
    const actionableTypes: ErrorType[] = [
      'VALIDATION_ERROR',
      'AUTHENTICATION_ERROR', 
      'NETWORK_ERROR'
    ]
    return actionableTypes.includes(type)
  }

  private isRecoverable(type: ErrorType): boolean {
    const recoverableTypes: ErrorType[] = [
      'NETWORK_ERROR',
      'REGISTRATION_ERROR',
      'DATABASE_ERROR'
    ]
    return recoverableTypes.includes(type)
  }
}

class ErrorHandlingService {
  private errorHistory: AppError[] = []

  createError(
    type: ErrorType,
    message: string,
    originalError?: Error,
    context?: Record<string, any>
  ): AppError {
    const appError = new AppError(type, message, originalError, context)

    this.logError(appError)
    this.errorHistory.push(appError)

    return appError
  }

  private logError(error: AppError): void {
    console.error(`[${error.type}] ${error.message}`, {
      timestamp: error.timestamp.toISOString(),
      context: error.context,
      originalError: error.originalError,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    })
  }

  handleSupabaseError(error: any, context?: Record<string, any>): AppError {
    console.error('Supabase error:', error)

    if (error?.code === 'PGRST116') {
      return this.createError('PERMISSION_ERROR', 'Row level security violation', error, context)
    }

    if (error?.code === '23505') {
      return this.createError('REGISTRATION_ERROR', 'Duplicate registration attempt', error, context)
    }

    if (error?.message?.includes('JWT')) {
      return this.createError('AUTHENTICATION_ERROR', 'Authentication token invalid', error, context)
    }

    if (error?.message?.includes('network')) {
      return this.createError('NETWORK_ERROR', 'Network connection failed', error, context)
    }

    return this.createError('DATABASE_ERROR', error?.message || 'Unknown database error', error, context)
  }

  handleNetworkError(error: Error, context?: Record<string, any>): AppError {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return this.createError('NETWORK_ERROR', 'Network request failed', error, context)
    }

    if (error.name === 'AbortError') {
      return this.createError('NETWORK_ERROR', 'Request timeout', error, context)
    }

    return this.createError('NETWORK_ERROR', 'Network error occurred', error, context)
  }

  handleValidationError(field: string, message: string, context?: Record<string, any>): AppError {
    return this.createError('VALIDATION_ERROR', `Validation failed for ${field}: ${message}`, undefined, {
      field,
      ...context
    })
  }

  getSuggestedActions(error: AppError): string[] {
    const actions: Record<ErrorType, string[]> = {
      NETWORK_ERROR: [
        'בדוק את החיבור לאינטרנט',
        'רענן את הדף',
        'נסה שוב בעוד מספר דקות'
      ],
      VALIDATION_ERROR: [
        'בדוק את השדות הנדרשים',
        'ודא שהמידע תקין',
        'נסה לשלוח שוב'
      ],
      AUTHENTICATION_ERROR: [
        'התחבר שוב למערכת',
        'נקה את הקאש של הדפדפן',
        'פנה למנהל המערכת'
      ],
      PERMISSION_ERROR: [
        'פנה למנהל המערכת',
        'בדוק שיש לך הרשאות מתאימות'
      ],
      REGISTRATION_ERROR: [
        'רענן את הדף',
        'בדוק אם המקום עדיין פנוי',
        'נסה שוב'
      ],
      DATABASE_ERROR: [
        'נסה שוב בעוד מספר דקות',
        'רענן את הדף',
        'פנה למנהל המערכת אם הבעיה נמשכת'
      ],
      UNKNOWN_ERROR: [
        'רענן את הדף',
        'נסה שוב',
        'פנה למנהל המערכת'
      ]
    }

    return actions[error.type] || actions.UNKNOWN_ERROR
  }

  shouldRetry(error: AppError): boolean {
    const retryableTypes: ErrorType[] = [
      'NETWORK_ERROR',
      'DATABASE_ERROR'
    ]
    return retryableTypes.includes(error.type)
  }

  getRetryDelay(attemptNumber: number): number {
    return Math.min(1000 * Math.pow(2, attemptNumber), 10000)
  }

  getRecentErrors(limit: number = 10): AppError[] {
    return this.errorHistory.slice(-limit)
  }

  clearErrorHistory(): void {
    this.errorHistory = []
  }
}

export const errorHandlingService = new ErrorHandlingService()

export const handleAsyncError = async <T>(
  operation: () => Promise<T>,
  context?: Record<string, any>
): Promise<T> => {
  try {
    return await operation()
  } catch (error) {
    if (error instanceof Error) {
      throw errorHandlingService.handleNetworkError(error, context)
    }
    throw errorHandlingService.createError('UNKNOWN_ERROR', 'Unknown async error', undefined, context)
  }
}

export const handleSupabaseOperation = async <T>(
  operation: () => Promise<{ data: T; error: any }>,
  context?: Record<string, any>
): Promise<T> => {
  try {
    const { data, error } = await operation()
    if (error) {
      throw errorHandlingService.handleSupabaseError(error, context)
    }
    return data
  } catch (error) {
    if (error instanceof AppError) {
      throw error
    }
    throw errorHandlingService.createError('DATABASE_ERROR', 'Supabase operation failed', error as Error, context)
  }
}
